import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { StatCategory } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { categoryLabels } from '@sorare/core/src/lib/scoring';

import { isGameScheduled } from '#src/shared/game/lib.tsx';
import { isCategoryDecisive } from '#src/shared/stats/PlayerGameScoreStats/utils.ts';

import type { CategoryHeader_playerGameScoreInterface } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  padding: var(--double-unit);
  &[role='button'] {
    cursor: pointer;
  }
  justify-content: space-between;
  &:hover {
    background-color: var(--c-nd-150-opaque);
  }
`;

const CategoryName = styled(BodyS)`
  &::first-letter {
    text-transform: uppercase;
  }
`;

const getScoreColor = (score: number | null) => {
  if (!score) {
    return 'var(--c-nd-600)';
  }
  if (score > 0) {
    return 'var(--c-score-high)';
  }
  return 'var(--c-score-veryLow)';
};

type Props = {
  category: StatCategory;
  playerGameScores: CategoryHeader_playerGameScoreInterface[];
  expanded: boolean;
  onClick: () => void;
};

export const CategoryHeader = ({
  category,
  playerGameScores,
  expanded,
  onClick,
}: Props) => {
  return (
    <Root role="button" onClick={onClick}>
      <Horizontal>
        <FontAwesomeIcon
          icon={expanded ? iconChevronUp : iconChevronDown}
          size="xs"
        />
        <CategoryName as="p" bold>
          {category in categoryLabels ? (
            <FormattedMessage
              {...categoryLabels[category as keyof typeof categoryLabels]}
            />
          ) : (
            category
          )}
        </CategoryName>
      </Horizontal>
      {playerGameScores.map(playerGameScore => {
        const categoryStats = playerGameScore.detailedScore.filter(
          statScore => statScore.category === category
        );
        const totalScore = categoryStats.reduce(
          (acc, statScore) =>
            acc +
            (isCategoryDecisive(statScore.category)
              ? statScore.statValue
              : statScore.totalScore),
          0
        );

        return (
          <BodyS
            key={playerGameScore.id}
            as="p"
            bold
            color={
              category === StatCategory.NEGATIVE_DECISIVE_STAT && totalScore > 0
                ? 'var(--c-score-veryLow)'
                : getScoreColor(totalScore)
            }
          >
            {category === StatCategory.NEGATIVE_DECISIVE_STAT && totalScore > 0
              ? '-'
              : ''}
            {categoryStats.length &&
            !isGameScheduled(playerGameScore.anyGame.status) ? (
              <FormattedNumber value={totalScore} maximumFractionDigits={2} />
            ) : (
              '-'
            )}
          </BodyS>
        );
      })}
    </Root>
  );
};

CategoryHeader.fragments = {
  playerGameScoreInterface: gql`
    fragment CategoryHeader_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      anyGame {
        id
        status: statusTyped
      }
      detailedScore {
        category
        totalScore
        statValue
      }
    }
  ` as TypedDocumentNode<CategoryHeader_playerGameScoreInterface>,
};
